import { useCallback, useEffect, useRef, useState } from 'react';
import { ThumbnailQueue } from '../helpers/thumbnailQueue';
import { getThumbs, putThumbs, thumbKey, touchDoc, type ThumbRecord } from '../helpers/thumbnailCache';

// В идеале бы побольше, но для скорости 130
export const THUMB_W = 130;

// В первую очередь влияет на размер
export const CANVAS_QUALITY = 0.3

// Насколько страниц вперёд забегаем за пределы видимой области.
const PREFETCH = 6;

// Потолок живых object URL. Больше держать незачем: невидимые миниатюры всё
// равно лежат в IndexedDB и поднимаются оттуда мгновенно.
const MAX_LIVE_URLS = 80;

const FLUSH_EVERY = 12;
const FLUSH_AFTER_MS = 600;

export interface Thumb {
    url: string;
    w: number;
    h: number;
}

interface Params {
    /** Тот же документ, что рендерит основной просмотрщик — второго getDocument нет. */
    pdfDocRef: React.MutableRefObject<any>;
    totalPages: number;
    /** Панель открыта и документ готов. */
    enabled: boolean;
    /** Запасной id, если у PDF нет fingerprint. Может быть 0 — тогда кэш только в памяти. */
    fallbackDocId?: number;
    currentPage: number;
}

let webpSupport: boolean | null = null;
function supportsWebp(): boolean {
    if (webpSupport === null) {
        try {
            const probe = document.createElement('canvas');
            probe.width = 1;
            probe.height = 1;
            webpSupport = probe.toDataURL('image/webp').startsWith('data:image/webp');
        } catch {
            webpSupport = false;
        }
    }
    return webpSupport;
}

function canvasToBuffer(canvas: HTMLCanvasElement, type: string): Promise<ArrayBuffer | null> {
    return new Promise((resolve) => {
        try {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        resolve(null);
                        return;
                    }
                    blob.arrayBuffer().then(resolve).catch(() => resolve(null));
                },
                type,
                CANVAS_QUALITY,
            );
        } catch {
            resolve(null);
        }
    });
}

export function usePdfThumbnails({ pdfDocRef, totalPages, enabled, fallbackDocId, currentPage }: Params) {
    const [thumbs, setThumbs] = useState<Map<number, Thumb>>(new Map());
    // Пропорции страницы 1. Задают высоту плейсхолдеров, чтобы лента не прыгала
    // по мере подгрузки — иначе скролл уезжает под пальцем.
    const [aspect, setAspect] = useState<number | null>(null);

    const queueRef = useRef<ThumbnailQueue | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);
    // Элементы, чей ref-колбэк уже сработал. React коммитит весь список кнопок
    // (и вызывает их ref-колбэки) за один проход ДО того, как этот useEffect
    // успевает создать IntersectionObserver — то есть при первом рендере панели
    // observerRef.current ещё null для каждой кнопки. Без этой карты ни одна
    // миниатюра не наблюдалась бы вообще: observer создаётся, но уже поздно —
    // ref-колбэки, которые могли бы его использовать, отработали раньше.
    const elementsRef = useRef<Map<number, HTMLElement>>(new Map());
    const visibleRef = useRef<Set<number>>(new Set());
    // Живые object URL в порядке создания — Map сохраняет порядок вставки, этого
    // достаточно для LRU без отдельной структуры.
    const urlsRef = useRef<Map<number, string>>(new Map());
    const thumbsRef = useRef<Map<number, Thumb>>(new Map());
    thumbsRef.current = thumbs;
    const currentPageRef = useRef(currentPage);
    currentPageRef.current = currentPage;

    // Сентинел поколения документа: у каждого прогона эффекта свой объект. Любая
    // асинхронная задача после await сверяется с ним и тихо выходит, если документ
    // успели сменить или панель закрыли.
    const genRef = useRef<object | null>(null);

    const writeBufRef = useRef<ThumbRecord[]>([]);
    const flushTimerRef = useRef<number | null>(null);
    const docIdRef = useRef<string | null>(null);

    const flushWrites = useCallback(() => {
        if (flushTimerRef.current !== null) {
            clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
        const docId = docIdRef.current;
        const batch = writeBufRef.current;
        writeBufRef.current = [];
        if (docId && batch.length) void putThumbs(docId, batch);
    }, []);

    const releaseUrls = useCallback(() => {
        for (const url of urlsRef.current.values()) URL.revokeObjectURL(url);
        urlsRef.current.clear();
    }, []);

    /** Вытеснить самые старые URL, не трогая видимые и текущую страницу. */
    const trimUrls = useCallback(() => {
        if (urlsRef.current.size <= MAX_LIVE_URLS) return;

        const protectedPages = new Set(visibleRef.current);
        protectedPages.add(currentPageRef.current);

        const doomed: number[] = [];
        for (const page of urlsRef.current.keys()) {
            if (urlsRef.current.size - doomed.length <= MAX_LIVE_URLS) break;
            if (protectedPages.has(page)) continue;
            doomed.push(page);
        }
        if (!doomed.length) return;

        for (const page of doomed) {
            const url = urlsRef.current.get(page);
            if (url) URL.revokeObjectURL(url);
            urlsRef.current.delete(page);
        }
        setThumbs((prev) => {
            const next = new Map(prev);
            for (const page of doomed) next.delete(page);
            return next;
        });
    }, []);

    const publish = useCallback(
        (page: number, buffer: ArrayBuffer, type: string, w: number, h: number) => {
            const url = URL.createObjectURL(new Blob([buffer], { type }));
            const previous = urlsRef.current.get(page);
            if (previous) URL.revokeObjectURL(previous);
            urlsRef.current.set(page, url);
            setThumbs((prev) => new Map(prev).set(page, { url, w, h }));
            trimUrls();
        },
        [trimUrls],
    );

    // Главный эффект: живёт ровно столько, сколько открыта панель поверх одного
    // документа. pdfReady сбрасывается в false в эффекте загрузки до destroy()
    // старого документа, поэтому смена url надёжно пересоздаёт всё это хозяйство.
    useEffect(() => {
        if (!enabled || !totalPages) return;

        const pdf = pdfDocRef.current;
        if (!pdf) return;

        const gen = {};
        genRef.current = gen;
        const alive = () => genRef.current === gen;

        const fingerprint: string | null = Array.isArray(pdf.fingerprints) ? pdf.fingerprints[0] : null;
        // fingerprint выводится из содержимого PDF: стабилен между сессиями и
        // одинаков в обоих приложениях, в отличие от url (это blob:-ссылка).
        docIdRef.current = fingerprint || (fallbackDocId ? `wia-${fallbackDocId}` : null);
        if (docIdRef.current) void touchDoc(docIdRef.current);

        const canvas = document.createElement('canvas');
        canvasRef.current = canvas;
        const queue = new ThumbnailQueue();
        queueRef.current = queue;

        const mimeType = supportsWebp() ? 'image/webp' : 'image/jpeg';

        const renderPage = async (page: number): Promise<void> => {
            if (!alive()) return;
            const doc = pdfDocRef.current;
            if (!doc) return;

            const pdfPage = await doc.getPage(page);
            if (!alive()) return;

            const base = pdfPage.getViewport({ scale: 1 });
            const viewport = pdfPage.getViewport({ scale: THUMB_W / base.width });
            const w = Math.max(1, Math.round(viewport.width));
            const h = Math.max(1, Math.round(viewport.height));

            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            ctx.clearRect(0, 0, w, h);

            // Отдельный canvas — обязательное условие. Guard в pdf.js стоит на
            // canvas (InternalRenderTask.#canvasInUse), а не на странице, поэтому
            // параллельный рендер той же страницы в основной просмотрщик безопасен.
            // renderTaskRef основного рендера здесь не трогаем никогда.
            await pdfPage.render({ canvasContext: ctx, viewport }).promise;
            if (!alive()) return;

            const buffer = await canvasToBuffer(canvas, mimeType);

            // Растеризация уже забрана в canvas -> буфер, декодированные картинки/шрифты
            // страницы в кэше pdf.js (pdfPage.objs) больше не нужны. Без этого вызова
            // они копятся НАВСЕГДА: основной просмотрщик чистит страницу при уходе с
            // неё (см. эффект рендера в index.tsx), а миниатюры открывают через getPage
            // сотни страниц подряд и ни одну не освобождают - на тяжёлых сканах
            // (крупные встроенные изображения) это и роняет вкладку по памяти.
            // Безопасно всегда: #tryCleanup сам не выполнится, пока идёт активный
            // render той же страницы (см. комментарий у renderTaskRef выше) - тогда
            // будет просто false, а не ошибка.
            pdfPage.cleanup();

            if (!alive() || !buffer) return;

            publish(page, buffer, mimeType, w, h);

            if (docIdRef.current) {
                writeBufRef.current.push({
                    key: thumbKey(docIdRef.current, THUMB_W, page),
                    docId: docIdRef.current,
                    buffer,
                    type: mimeType,
                    w,
                    h,
                });
                if (writeBufRef.current.length >= FLUSH_EVERY) flushWrites();
                else if (flushTimerRef.current === null) {
                    flushTimerRef.current = window.setTimeout(flushWrites, FLUSH_AFTER_MS);
                }
            }
        };

        // Пересчёт «что нужно показать» по текущему видимому окну: сначала одним
        // запросом поднимаем всё, что уже в кэше, и только промахи ставим в очередь.
        const sync = async () => {
            if (!alive()) return;

            const visible = visibleRef.current;
            if (!visible.size) return;

            const from = Math.max(1, Math.min(...visible) - 2);
            const to = Math.min(totalPages, Math.max(...visible) + PREFETCH);
            const anchor = Math.min(...visible);

            const wanted: number[] = [];
            for (let page = from; page <= to; page += 1) {
                if (!thumbsRef.current.has(page)) wanted.push(page);
            }

            queue.retainOnly(new Set(wanted.map(String)));
            if (!wanted.length) return;

            const docId = docIdRef.current;
            const cached = docId
                ? await getThumbs(wanted.map((page) => thumbKey(docId, THUMB_W, page)))
                : new Map<string, ThumbRecord>();
            if (!alive()) return;

            for (const page of wanted) {
                const hit = docId ? cached.get(thumbKey(docId, THUMB_W, page)) : undefined;
                if (hit) {
                    publish(page, hit.buffer, hit.type, hit.w, hit.h);
                    continue;
                }
                queue.push(String(page), Math.abs(page - anchor), () => renderPage(page));
            }
        };

        // Дебаунс, а не microtask: при быстром скролле IntersectionObserver
        // срабатывает почти на каждый кадр, и каждый вызов sync() выбрасывает из
        // очереди ещё не начатые задачи под новое окно (см. retainOnly выше). Если
        // пересчитывать на КАЖДОЕ срабатывание, окно меняется быстрее, чем очередь
        // (concurrency 1) успевает хоть что-то отрендерить - до финиша долистывают
        // единицы случайных страниц, а не всё видимое окно. Ждём короткую паузу в
        // событиях скролла и пересчитываем один раз по её итогу.
        let syncTimer: ReturnType<typeof setTimeout> | null = null;
        const requestSync = () => {
            if (syncTimer !== null) clearTimeout(syncTimer);
            syncTimer = setTimeout(() => {
                syncTimer = null;
                void sync();
            }, 80);
        };

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const page = Number((entry.target as HTMLElement).dataset.page);
                    if (!page) continue;
                    if (entry.isIntersecting) visibleRef.current.add(page);
                    else visibleRef.current.delete(page);
                }
                requestSync();
            },
            // Запас в 400px: миниатюры успевают дорисоваться до того, как строка
            // реально доедет до экрана.
            { root: null, rootMargin: '400px 0px', threshold: 0 },
        );
        observerRef.current = observer;
        // Кнопки уже смонтированы к этому моменту (эффект коммитится после
        // разметки) — их ref-колбэки сработали, когда observerRef.current
        // ещё был null, и не наблюдали ничего. Наблюдаем задним числом.
        for (const el of elementsRef.current.values()) observer.observe(el);

        // Пропорции берём с первой страницы: в подавляющем большинстве документов
        // формат одинаков, а плейсхолдерам нужна высота до всякого рендера.
        void pdf.getPage(1).then((page: any) => {
            if (!alive()) return;
            const viewport = page.getViewport({ scale: 1 });
            setAspect(viewport.width / viewport.height);
        }).catch(() => {});

        return () => {
            genRef.current = null;
            flushWrites();
            queue.dispose();
            observer.disconnect();
            observerRef.current = null;
            queueRef.current = null;
            canvasRef.current = null;
            visibleRef.current = new Set();
            // Сначала убираем URL из состояния, потом отзываем: обратный порядок
            // оставляет на один кадр <img> с уже отозванным src — битая картинка.
            setThumbs(new Map());
            releaseUrls();
            setAspect(null);
        };
    }, [enabled, totalPages, pdfDocRef, fallbackDocId, publish, flushWrites, releaseUrls]);

    // Стабильный ref-колбэк: номер страницы едет в data-атрибуте, поэтому колбэк
    // не пересоздаётся на каждый рендер. Элемент регистрируется в elementsRef
    // независимо от того, создан ли уже observer (см. комментарий у elementsRef) —
    // и наблюдается сразу, если создан. Cleanup-функция (React 19) снимает
    // наблюдение и вычищает карту при размонтировании кнопки.
    const itemRef = useCallback((el: HTMLElement | null) => {
        if (!el) return;
        const page = Number(el.dataset.page);
        if (!page) return;

        elementsRef.current.set(page, el);
        observerRef.current?.observe(el);

        return () => {
            elementsRef.current.delete(page);
            observerRef.current?.unobserve(el);
        };
    }, []);

    // Лента на 300 страниц — это ~65 000px прокрутки. Бегунок скроллбара при
    // такой длине вырождается в минимальные ~17px, и один его пиксель равен
    // почти половине строки: точно позиционироваться им невозможно, а доехать
    // колесом до края — это ~650 щелчков. Поэтому смена страницы снаружи
    // (стрелки, поле ввода номера в тулбаре, Home/End) сама подтягивает ленту к
    // нужной строке, и скроллбар перестаёт быть единственным способом навигации.
    //
    // block: 'nearest' не трогает прокрутку, если строка и так видна, — значит
    // ручное листание панели и клики по самим миниатюрам ничего не дёргают.
    useEffect(() => {
        if (!enabled) return;
        elementsRef.current.get(currentPage)?.scrollIntoView({ block: 'nearest' });
    }, [currentPage, enabled]);

    return { thumbs, aspect, itemRef };
}
